import { DynamicPage, DynamicPageTag } from '@betterfunnel/types';
import {
  ArrowDownOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AnalyticsTypeButton,
  AnalyticsTypeOption,
} from '../components/AnalyticsTypeButton';
import AlertModal from '../components/Common/AlertModal';
import Button from '../components/Common/Button';
import MenuButton from '../components/Common/MenuButton';
import TextField from '../components/Common/TextField';
import DashboardAnalyticsRangeSelector from '../components/DashboardAnalyticsRangeSelector';
import {
  DynamicPagesTable,
  DynamicPagesTableHandle,
} from '../components/DynamicPagesTable';
import {
  FilterButton,
  FilterButtonOption,
} from '../components/FilterButton/FilterButton';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { useDebounce } from '../hooks/useDebounce';
import CreateDynamicPageModal from '../modals/CreateDynamicPageModal';
import BulkPublishDynamicPagesModal from '../modals/DynamicPageUpdateTools/BulkPublishDynamicPagesModal';
import UpdateDynamicPagesLinksModal from '../modals/DynamicPageUpdateTools/UpdateDynamicPagesLinksModal';
import UpdateDynamicPagesTagsModal from '../modals/DynamicPageUpdateTools/UpdateDynamicPagesTagsModal';
import ExportAnalyticsModal from '../modals/ExportAnalyticsModal';
import { duplicateDynamicPage } from '../services/DynamicPagesService';
import { track } from '../utils/analytics';

const DYNAMIC_PAGES_CONFIG = 'DYNAMIC_PAGES_CONFIG';

interface DynamicPageFilters {
  tags: DynamicPageTag[];
  statuses: (DynamicPage['status'] | 'inactive')[];
  changes: boolean[];
}

type PartialDynamicPageFilters = {
  tags?: string[];
  statuses?: DynamicPageFilters['statuses'];
  changes?: boolean[];
};

function filterFromSearchParams(
  searchParams: URLSearchParams,
): PartialDynamicPageFilters {
  const filters: PartialDynamicPageFilters = {};

  const tags =
    searchParams.get('tags') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:tags`);
  if (tags) {
    try {
      filters.tags = JSON.parse(tags);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing tags from search params', e);
    }
  }

  const statuses =
    searchParams.get('statuses') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:statuses`);
  if (statuses) {
    try {
      filters.statuses = JSON.parse(statuses);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing statuses from search params', e);
    }
  }

  const changes =
    searchParams.get('changes') ||
    localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:changes`);
  if (changes) {
    try {
      filters.changes = JSON.parse(changes);
    } catch (e) {
      Sentry.captureException(e);
      console.error('Error parsing changes from search params', e);
    }
  }

  return filters;
}

export default function DynamicPages() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tableRef = useRef<DynamicPagesTableHandle>(null);

  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { dynamicPages, analyticsType, setAnalyticsType } =
    useContext(DashboardContext);
  // const { customRangeAnalytics } = useContext(OverviewContext);

  const [createDynamicPageDialogOpen, setCreateDynamicPageDialogOpen] =
    useState(false);
  const [exportAnalyticsDialogOpen, setExportAnalyticsDialogOpen] =
    useState(false);
  const [duplicateDynamicPageModal, setDuplicateDynamicPageModal] = useState<{
    dynamicPage: QueryDocumentSnapshot<DynamicPage, DynamicPage>;
    loading?: boolean;
  }>();
  const [enableRowSelection, setEnableRowSelection] = useState(false);
  const [rowsSelected, setRowsSelected] = useState<Record<string, boolean>>({});
  const [updateDynamicPagesTagsModalOpen, setUpdateDynamicPagesTagsModalOpen] =
    useState(false);
  const [
    updateDynamicPagesLinksModalOpen,
    setUpdateDynamicPagesLinksModalOpen,
  ] = useState(false);
  const [
    bulkPublishDynamicPagesModalOpen,
    setBulkPublishDynamicPagesModalOpen,
  ] = useState(false);

  const [searchQuery, setSearchQuery] = useState(
    (searchParams.get('search') ||
      localStorage.getItem(`${DYNAMIC_PAGES_CONFIG}:search`)) ??
      '',
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [filters, setFilters] = useState<PartialDynamicPageFilters>(
    filterFromSearchParams(searchParams),
  );

  useEffect(() => {
    setSearchParams((params) => {
      if (debouncedSearchQuery) {
        params.set('search', debouncedSearchQuery);
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:search`,
          debouncedSearchQuery,
        );
      } else {
        params.delete('search');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:search`);
      }

      if (filters.tags?.length) {
        params.set('tags', JSON.stringify(filters.tags));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:tags`,
          JSON.stringify(filters.tags),
        );
      } else {
        params.delete('tags');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:tags`);
      }

      if (filters.statuses?.length) {
        params.set('statuses', JSON.stringify(filters.statuses));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:statuses`,
          JSON.stringify(filters.statuses),
        );
      } else {
        params.delete('statuses');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:statuses`);
      }

      if (filters.changes?.length) {
        params.set('changes', JSON.stringify(filters.changes));
        localStorage.setItem(
          `${DYNAMIC_PAGES_CONFIG}:changes`,
          JSON.stringify(filters.changes),
        );
      } else {
        params.delete('changes');
        localStorage.removeItem(`${DYNAMIC_PAGES_CONFIG}:changes`);
      }

      return new URLSearchParams(params);
    });
  }, [debouncedSearchQuery, filters, setSearchParams]);

  const filteredDynamicPages = useMemo(() => {
    const filtered = dynamicPages?.filter((c) => {
      const campaignData = c.data();
      if (debouncedSearchQuery && debouncedSearchQuery.length > 2) {
        if (
          !campaignData.name
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) &&
          !campaignData.path
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())
        ) {
          return false;
        }
      }

      if (filters.tags?.length) {
        if (
          !campaignData.tags?.some((t) =>
            filters.tags?.some((tag) => tag === t.id),
          )
        ) {
          return false;
        }
      }

      if (filters.statuses?.length) {
        const campaignStatus = campaignData.status;
        if (!filters.statuses?.includes(campaignStatus)) {
          return false;
        }
      }

      if (filters.changes?.length) {
        const hasChanges = campaignData.hasChangesToPublish;
        if (!filters.changes?.includes(hasChanges ?? false)) {
          return false;
        }
      }

      return true;
    });

    return filtered;
  }, [dynamicPages, debouncedSearchQuery, filters]);

  const filterOptions = useMemo(() => {
    const tagsMap: Map<string, DynamicPageTag> = new Map();
    dynamicPages?.forEach((c) => {
      c.data().tags?.forEach((t) => {
        tagsMap.set(t.id, t);
      });
    });

    const _filters: DynamicPageFilters = {
      tags: Array.from(tagsMap.values()),
      statuses: ['active', 'inactive'],
      changes: [true, false],
    };

    return _filters;
  }, [dynamicPages]);

  return (
    <>
      <CreateDynamicPageModal
        open={createDynamicPageDialogOpen}
        onClose={() => setCreateDynamicPageDialogOpen(false)}
      />
      <ExportAnalyticsModal
        open={exportAnalyticsDialogOpen}
        onClose={() => setExportAnalyticsDialogOpen(false)}
      />
      <AlertModal
        type="info"
        title={`Are you sure you'd like to duplicate ${
          duplicateDynamicPageModal?.dynamicPage.data()?.name
        }?`}
        description="This will create a new dynamic page with all the same settings and variants."
        open={!!duplicateDynamicPageModal}
        onClose={() => {
          if (duplicateDynamicPageModal?.loading) return;
          setDuplicateDynamicPageModal(undefined);
        }}
        actions={[
          {
            title: 'Cancel',
            disabled: duplicateDynamicPageModal?.loading,
            type: 'secondary',
            onClick: () => {
              setDuplicateDynamicPageModal(undefined);
            },
          },
          {
            title: 'Duplicate',
            disabled: duplicateDynamicPageModal?.loading,
            type: 'primary',
            loading: duplicateDynamicPageModal?.loading,
            onClick: async () => {
              const config = duplicateDynamicPageModal;
              if (!config || !selectedAccount) return;

              track('duplicate_campaign_button_click', {
                dynamicPageId: config.dynamicPage.id,
              });

              setDuplicateDynamicPageModal({ ...config, loading: true });
              duplicateDynamicPage(selectedAccount.id, config.dynamicPage.id)
                .then(({ campaignId }) => {
                  setDuplicateDynamicPageModal(undefined);
                  navigate(`/workspace/${slug}/pages/${campaignId}`);
                })
                .catch((e) => {
                  setDuplicateDynamicPageModal({ ...config, loading: false });
                  show('error duplicating dynamic page', e);
                  Sentry.captureException(e);
                });
            },
          },
        ]}
      />
      <UpdateDynamicPagesTagsModal
        open={updateDynamicPagesTagsModalOpen}
        onClose={() => setUpdateDynamicPagesTagsModalOpen(false)}
        dynamicPages={
          Object.entries(rowsSelected)
            .map(([id, selected]) =>
              selected ? dynamicPages?.find((c) => c.id === id) : null,
            )
            .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[]
        }
      />
      <UpdateDynamicPagesLinksModal
        open={updateDynamicPagesLinksModalOpen}
        onClose={() => setUpdateDynamicPagesLinksModalOpen(false)}
        dynamicPages={
          Object.entries(rowsSelected)
            .map(([id, selected]) =>
              selected ? dynamicPages?.find((c) => c.id === id) : null,
            )
            .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[]
        }
      />
      <BulkPublishDynamicPagesModal
        open={bulkPublishDynamicPagesModalOpen}
        onClose={() => setBulkPublishDynamicPagesModalOpen(false)}
        dynamicPages={
          Object.entries(rowsSelected)
            .map(([id, selected]) =>
              selected ? dynamicPages?.find((c) => c.id === id) : null,
            )
            .filter(Boolean) as QueryDocumentSnapshot<DynamicPage>[]
        }
      />
      <div className="flex h-full flex-col">
        <div className="flex flex-col items-stretch sm:flex-row sm:items-center">
          <div className="flex h-12 w-full flex-row items-center justify-between gap-2 border-b border-gray-300 pb-3">
            <h1 className="flex-grow text-2xl font-semibold leading-6 text-gray-900">
              Dynamic Pages
            </h1>
            <div className="flex flex-row gap-2">
              <Button
                size="normal"
                className="shrink-0"
                onClick={() => {
                  track('new_campaign_button_click');
                  setCreateDynamicPageDialogOpen(true);
                }}
                icon={<PlusIcon className="h-4 w-4" />}
              >
                New Page
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-1 flex-col gap-3">
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-start lg:items-center lg:gap-4">
            <TextField
              startIcon={
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
              }
              className="h-8 w-full lg:w-52"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              placeholder="Search"
            />
            <div className="shrink-0">
              <FilterButton>
                <FilterButtonOption
                  title="Status"
                  selected={filters.statuses ?? []}
                  options={filterOptions.statuses}
                  getOptionKey={(option) => option}
                  getOptionLabel={(option) => option}
                  onChange={(selected) => {
                    setFilters((filters) => ({
                      ...filters,
                      statuses: selected as DynamicPageFilters['statuses'],
                    }));
                  }}
                />
                <FilterButtonOption<boolean>
                  title="Changes"
                  selected={filters.changes?.map((a) => a.toString()) ?? []}
                  options={filterOptions.changes}
                  getOptionKey={(option) => `${option}`}
                  getOptionLabel={(option) => (option ? 'Pending' : 'Deployed')}
                  onChange={(selected) => {
                    setFilters((filters) => ({
                      ...filters,
                      changes: selected.map((a) => a === 'true'),
                    }));
                  }}
                />
                <FilterButtonOption
                  title="Tags"
                  selected={filters.tags ?? []}
                  options={filterOptions.tags}
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  onChange={(selected) => {
                    setFilters((filters) => ({
                      ...filters,
                      tags: selected,
                    }));
                  }}
                  noOptionsMessage="No tags found"
                />
              </FilterButton>
            </div>
            {/* <div className="shrink-0">
              <SortButton
                sortOptions={[
                  null,
                  'visitors',
                  'conversion',
                  'conversion rate',
                ]}
                sort={sort}
                onChange={(sort: DynamicPageSortOption | null) => {
                  setSort(sort);
                }}
              />
            </div> */}
            <div className="shrink-0">
              <AnalyticsTypeButton
                analyticsTypeOptions={[null, 'device']}
                analyticsType={analyticsType}
                onChange={(analyticsType: AnalyticsTypeOption | null) => {
                  setAnalyticsType(analyticsType);
                }}
              />
            </div>
            <Button
              variant="secondary"
              color="white"
              onClick={() => {
                track('export_analytics_button_click');
                setExportAnalyticsDialogOpen(true);
              }}
              icon={<ArrowDownOnSquareIcon className="h-4 w-4" />}
            >
              Export Analytics
            </Button>
            <div className="hidden flex-1 lg:block" />
            <div className="shrink-0">
              <DashboardAnalyticsRangeSelector size="small" variant="text" />
            </div>
          </div>
          {enableRowSelection && (
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-start lg:items-center lg:gap-4">
              <Button
                variant="ternary"
                color="primary"
                onClick={() => {
                  setUpdateDynamicPagesTagsModalOpen(true);
                }}
                disabled={!Object.keys(rowsSelected).length}
              >
                Update Tags
              </Button>
              <Button
                variant="ternary"
                color="primary"
                onClick={() => {
                  setUpdateDynamicPagesLinksModalOpen(true);
                }}
                disabled={!Object.keys(rowsSelected).length}
              >
                Update Links
              </Button>
              <Button
                variant="ternary"
                color="primary"
                onClick={() => {
                  setBulkPublishDynamicPagesModalOpen(true);
                }}
                disabled={!Object.keys(rowsSelected).length}
              >
                Bulk Auto Publish
              </Button>

              <Button
                variant="ternary"
                color="primary"
                onClick={() => {
                  tableRef.current
                    ?.getTableInstance()
                    ?.toggleAllRowsSelected(false);
                  setEnableRowSelection(false);
                }}
              >
                Cancel Selection
              </Button>
            </div>
          )}
          <div className="relative flex-1">
            <DynamicPagesTable
              ref={tableRef}
              items={filteredDynamicPages}
              onRowClick={(dynamicPage) => {
                track('campaign_row_click', {
                  dynamicPageId: dynamicPage.id,
                });
                navigate(`/workspace/${slug}/pages/${dynamicPage.id}`);
              }}
              enableRowSelection={enableRowSelection}
              onRowSelectionChange={(selected) => {
                setRowsSelected(
                  Object.fromEntries(selected.map((s) => [s.id, true])),
                );
              }}
              actions={(dynamicPage) => (
                <MenuButton
                  items={[
                    {
                      title: 'Duplicate',
                      onClick: () => {
                        setDuplicateDynamicPageModal({
                          dynamicPage: dynamicPage,
                        });
                      },
                    },
                    {
                      title: 'Select',
                      onClick: () => {
                        setEnableRowSelection(true);
                        setTimeout(() => {
                          tableRef.current
                            ?.getTableInstance()
                            ?.getRowModel()
                            .rows.find((r) => r.original.id === dynamicPage.id)
                            ?.toggleSelected(true);
                        }, 0);
                      },
                    },
                    {
                      title: 'Open in new tab',
                      onClick: () => {
                        window.open(
                          `/workspace/${slug}/pages/${dynamicPage.id}`,
                          '_blank',
                        );
                      },
                    },
                  ]}
                />
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// type DynamicPageSortOption =
//   | 'visitors'
//   | 'conversion'
//   | 'conversion rate'
//   | null;

// interface SortButtonProps {
//   sortOptions: DynamicPageSortOption[];
//   sort: DynamicPageSortOption;
//   onChange: (sort: DynamicPageSortOption) => void;
// }

// const SortButton = ({ sortOptions, sort, onChange }: SortButtonProps) => {
//   return (
//     <Popover className="relative">
//       <PopoverButton
//         as={Button}
//         className="w-full"
//         variant="secondary"
//         color="white"
//         size="normal"
//         onClick={() => {}}
//         icon={<ArrowsUpDownIcon className="h-4 w-4" />}
//       >
//         Sort{sort ? ` / ${capitalize(sort)}` : ''}
//       </PopoverButton>
//       <PopoverPanel
//         transition
//         anchor="bottom end"
//         modal
//         className="z-10 transition duration-200 ease-in-out data-[closed]:translate-y-1 data-[closed]:opacity-0"
//       >
//         <div className="absolute right-0 z-20 ml-[1.2rem] mr-3.5 mt-0.5 h-4 w-4 rotate-45 border-l border-t border-gray-300 bg-white dark:border-slate-600 dark:bg-slate-800"></div>
//         <div className="mt-2.5 rounded-lg border border-gray-300 bg-white p-3 shadow-sm">
//           <div className="relative flex flex-col gap-4">
//             {sortOptions.map((option) => {
//               const selected = sort === option;

//               return (
//                 <Button
//                   key={option}
//                   variant={selected ? 'primary' : 'secondary'}
//                   className="justify-start pe-9"
//                   onClick={() => {
//                     onChange(option);
//                   }}
//                 >
//                   <CheckIcon
//                     className={classNames(
//                       'absolute right-2 h-4 w-4',
//                       selected ? 'visible' : 'invisible',
//                     )}
//                   />
//                   {capitalize(option || 'None')}
//                 </Button>
//               );
//             })}
//           </div>
//         </div>
//       </PopoverPanel>
//     </Popover>
//   );
// };
